import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Copy, Check, ExternalLink, Smartphone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InviteCoworkerDialog — generates an agency-scoped public registration link.
 *
 * Two modes:
 *  1. Scoped:   pass `agencyId` (and `agencyName`) directly — used by the
 *               in-app "Invite a coworker" flow for logged-in agency users.
 *  2. Selector: pass a non-empty `agencies` array and leave `agencyId` empty —
 *               used by super admins who want to pick which agency to invite to
 *               (e.g., from the Users page header).
 */
export default function InviteCoworkerDialog({
  open,
  onOpenChange,
  agencyId,
  agencyName,
  agencies, // optional: [{id, name}, ...]
}) {
  const [copied, setCopied] = useState(false);
  const [pickedId, setPickedId] = useState("");

  // Reset picker each time the dialog opens.
  useEffect(() => {
    if (open && !agencyId) setPickedId("");
  }, [open, agencyId]);

  const effectiveId = agencyId || pickedId;
  const effectiveName =
    agencyName ||
    (agencies && pickedId
      ? agencies.find((a) => a.id === pickedId)?.name
      : null);

  const link = useMemo(() => {
    if (!effectiveId) return "";
    return `${BACKEND_URL}/register/${effectiveId}`;
  }, [effectiveId]);

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // no-op
    }
  };

  const showSelector = !agencyId && Array.isArray(agencies) && agencies.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="invite-coworker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-amber-600" />
            Invite a distributor
          </DialogTitle>
          <DialogDescription>
            Share this link with anyone at a distributor (or a sister branch).
            They&apos;ll fill out the registration form and{" "}
            {effectiveName || "the rep agency"} will approve.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {showSelector && (
            <div className="bg-slate-50 border border-border rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data mb-1.5">
                Rep agency
              </div>
              <Select value={pickedId} onValueChange={setPickedId}>
                <SelectTrigger
                  className="rounded-sm bg-white"
                  data-testid="invite-agency-select"
                >
                  <SelectValue placeholder="Pick a rep agency…" />
                </SelectTrigger>
                <SelectContent>
                  {agencies.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-muted-foreground mt-1.5">
                Registrations from this link will land in that agency&apos;s approval queue.
              </div>
            </div>
          )}

          {/* Web link */}
          <div className="bg-slate-50 border border-border rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data mb-1.5">
              Registration link
            </div>
            <div className="flex items-center gap-2">
              <code
                className="flex-1 text-xs bg-white border border-border rounded-sm px-2 py-1.5 truncate font-mono-data text-slate-700"
                data-testid="invite-link"
              >
                {link || "— select an agency above —"}
              </code>
              <Button
                size="sm"
                onClick={copy}
                disabled={!link}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 shrink-0"
                data-testid="invite-copy-btn"
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5 mr-1.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy
                  </>
                )}
              </Button>
            </div>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800"
                data-testid="invite-open-btn"
              >
                Preview the form <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {/* App badges (placeholders until apps ship) */}
          <div className="bg-slate-50 border border-border rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data mb-2 flex items-center gap-1.5">
              <Smartphone className="w-3 h-3" />
              Or download the app
            </div>
            <div className="grid grid-cols-2 gap-2">
              <AppBadge platform="Apple" />
              <AppBadge platform="Google" />
            </div>
            <p className="mt-2 text-[10px] text-muted-foreground italic">
              The {effectiveName || "agency"} mobile app is on the way — coming
              soon to both stores.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppBadge({ platform }) {
  return (
    <div
      className="bg-slate-900 text-white rounded-sm px-3 py-2 flex items-center gap-2 opacity-70"
      data-testid={`app-badge-${platform.toLowerCase()}`}
    >
      <div className="text-[9px] uppercase tracking-widest opacity-70 font-mono-data">
        Coming soon
      </div>
      <div className="ml-auto text-xs font-display font-semibold">
        {platform === "Apple" ? "App Store" : "Google Play"}
      </div>
    </div>
  );
}
